import { getGtagAttributionParams } from '@/lib/attribution'

type Gtag = (...args: unknown[]) => void

function gtag(): Gtag | undefined {
  return typeof window === 'undefined' ? undefined : (window as unknown as { gtag?: Gtag }).gtag
}

/** GA4 `lead_submit` with first-touch attribution — call right after a form's backend submit succeeds. */
export function trackLeadSubmit(label: string, category = 'Form', details: Record<string, string> = {}): void {
  gtag()?.('event', 'lead_submit', {
    event_category: category,
    event_label: label,
    ...getGtagAttributionParams(),
    ...details,
  })
}

/** Doctor onboarding (/fr/configurer-assistant): first time each step is reached, to see where doctors stop. */
export function trackOnboardingStep(stepNumber: number, stepTitle: string): void {
  gtag()?.('event', 'onboarding_step', {
    step_number: stepNumber,
    step_title: stepTitle,
    ...getGtagAttributionParams(),
  })
}

/** Doctor onboarding received by the backend: the event to mark as a key event in GA4. */
export function trackOnboardingComplete(): void {
  gtag()?.('event', 'onboarding_complete', {
    event_category: 'Onboarding',
    ...getGtagAttributionParams(),
  })
}

export function trackWhatsAppClick(url: string): void {
  if (typeof window === 'undefined') return
  gtag()?.('event', 'whatsapp_click', {
    link_url: url,
    page_path: window.location.pathname,
    ...getGtagAttributionParams(),
  })
}

/** One delegated listener for every <a href="…wa.me…"> on the site. Returns a cleanup fn. */
export function listenForWhatsAppClicks(): () => void {
  const onClick = (e: MouseEvent) => {
    const link = (e.target as Element | null)?.closest?.('a')
    const url = link?.getAttribute('href')
    if (url && /(^|\/\/)(wa\.me|api\.whatsapp\.com)\//.test(url)) trackWhatsAppClick(url)
  }
  document.addEventListener('click', onClick)
  return () => document.removeEventListener('click', onClick)
}

/** A demo click is an engagement event, separate from a successful lead submission. */
export function listenForDemoRequestClicks(): () => void {
  const onClick = (event: MouseEvent) => {
    const anchor = (event.target as Element | null)?.closest?.('a')
    if (!anchor) return
    const target = new URL(anchor.href, window.location.href)
    const service = target.searchParams.get('service')
    if (target.origin !== window.location.origin || !/^\/(?:fr\/|ar\/|en\/)?(?:contact|book-demo)$/.test(target.pathname) || !service) return
    gtag()?.('event', 'demo_request_click', {
      requested_service: service,
      ...getGtagAttributionParams(),
    })
  }
  document.addEventListener('click', onClick)
  return () => document.removeEventListener('click', onClick)
}
